/**
 * ModerationErrorState Component
 *
 * Displays a user-friendly error state for moderation pages when
 * GraphQL/API errors are returned by hooks.
 *
 * Follows the domain/error-handling.md Component Error Handling pattern
 * with DomainError[] and onRetry support.
 */

import { formatErrorMessages } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain/errors';
import { Button, ButtonType, Heading, Text } from '@soteria/design-system-ve';

import * as styles from './ModerationErrorBoundary.css';

interface ModerationErrorStateProps {
  /** Array of domain errors to display */
  errors: DomainError[];

  /** Function to call when the retry button is clicked */
  onRetry: () => void;

  /** Optional title to display above the errors */
  title?: string;
}

/**
 * Component displayed when there's an error loading moderation data.
 * Renders within the page layout and provides a retry action.
 */
export function ModerationErrorState({ errors, onRetry, title = 'Error Loading Moderation Data' }: ModerationErrorStateProps) {
  if (!errors || errors.length === 0) {
    return null;
  }

  const errorMessages = formatErrorMessages(errors);
  const firstError = errors[0];

  return (
    <div className={styles.errorContainer}>
      <div className={styles.errorContent}>
        <Heading className={styles.errorTitle} level="h2">
          {title}
        </Heading>

        {errorMessages.length === 1 ? (
          <Text className={styles.errorDescription}>{errorMessages[0]}</Text>
        ) : (
          <ul>
            {errorMessages.map(message => (
              <li key={`error-${message}`}>
                <Text className={styles.errorDescription}>{message}</Text>
              </li>
            ))}
          </ul>
        )}

        <Text className={styles.errorHelp}>
          Error category: {firstError.category}, Code: {firstError.code}
        </Text>

        <div className={styles.errorActions}>
          <Button aria-label="Retry loading moderation data" buttonType={ButtonType.Primary} onClick={onRetry}>
            Retry
          </Button>
        </div>
      </div>
    </div>
  );
}
